import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useTeacherPreview, usePreviewPath } from '@/contexts/TeacherPreviewContext'
import { supabase } from '@/lib/supabase'
import type { Exercise, Content, Lesson, Unit } from '@/types'
import AudioPlayer from '@/components/common/AudioPlayer'
import {
  ArrowLeft,
  CheckCircle,
  ChevronRight,
  Trophy,
  RotateCcw,
  Headphones,
  PenTool,
  BookOpenCheck,
  BookOpen,
  Volume2
} from 'lucide-react'
import EditableText from '@/components/edit/EditableText'
import {
  checkFillBlank,
  checkMatching,
  checkOrdering,
  checkTranslation
} from '@/lib/exerciseUtils'
import {
  triggerContentComplete,
  triggerLessonComplete,
  triggerUnitComplete,
  triggerPerfectLesson,
} from '@/lib/flowTrigger'
import MultipleChoiceExercise from './exercises/MultipleChoiceExercise'
import FillBlankExercise from './exercises/FillBlankExercise'
import MatchingExercise from './exercises/MatchingExercise'
import OrderingExercise from './exercises/OrderingExercise'
import TranslationExercise from './exercises/TranslationExercise'
import ListeningExercise from './exercises/ListeningExercise'

interface ExerciseWithContent extends Exercise {
  content?: Content & {
    lesson?: Lesson & {
      unit?: Unit
    }
  }
}

export default function ExerciseView() {
  const { exerciseId } = useParams<{ exerciseId: string }>()
  const { user, student, isInCohort } = useAuth()
  const { isTeacherPreview } = useTeacherPreview()
  const previewPath = usePreviewPath()
  const navigate = useNavigate()

  const [exercise, setExercise] = useState<ExerciseWithContent | null>(null)
  const [allExercises, setAllExercises] = useState<Exercise[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState<string>('')
  const [textAnswer, setTextAnswer] = useState('')
  const [matchingPairs, setMatchingPairs] = useState<Record<string, string>>({})
  const [orderingItems, setOrderingItems] = useState<string[]>([])
  const [matchingDetails, setMatchingDetails] = useState<Record<string, boolean>>({})
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [score, setScore] = useState(0)
  const [correctCount, setCorrectCount] = useState(0)
  const [showResults, setShowResults] = useState(false)
  const [isCalculating, setIsCalculating] = useState(false)
  const [contentId, setContentId] = useState<string | null>(null)

  useEffect(() => {
    if (exerciseId) {
      loadExercise()
    }
  }, [exerciseId])

  const loadExercise = async () => {
    if (!exerciseId) return

    setIsLoading(true)
    try {
      const { data: exerciseData, error: exerciseError } = await supabase
        .from('exercises')
        .select(`*, content:contents(*, lesson:lessons(*, unit:units(*)))`)
        .eq('id', exerciseId)
        .single()

      if (exerciseError) throw exerciseError
      const exerciseWithContent = exerciseData as unknown as ExerciseWithContent

      // Premium gate: check if exercise belongs to free lesson (no aplica en previsualización)
      const isPremium = student?.subscription_status === 'active'
      const hasFullAccess = isPremium && isInCohort
      if (!hasFullAccess && !isTeacherPreview) {
        const unitOrder = exerciseWithContent.content?.lesson?.unit?.order_num
        const lessonOrder = exerciseWithContent.content?.lesson?.order_num
        if (unitOrder !== 1 || lessonOrder !== 1) {
          navigate('/units', { replace: true })
          return
        }
      }

      setExercise(exerciseWithContent)
      setContentId(exerciseWithContent.content_id)

      const { data: allExercisesData } = await supabase
        .from('exercises')
        .select('*')
        .eq('content_id', exerciseWithContent.content_id)
        .eq('is_active', true)
        .order('order_index', { ascending: true })

      if (allExercisesData) {
        const skillOrder = ['listening', 'writing', 'reading']
        const exercises = allExercisesData as unknown as Exercise[]
        const sortedExercises = exercises.sort((a, b) => {
          const aIndex = skillOrder.indexOf(a.skill)
          const bIndex = skillOrder.indexOf(b.skill)
          if (aIndex !== bIndex) return aIndex - bIndex
          return ((a as any).order_index || 0) - ((b as any).order_index || 0)
        })

        setAllExercises(sortedExercises)
        const index = sortedExercises.findIndex(e => e.id === exerciseId)
        setCurrentIndex(index >= 0 ? index : 0)
      }
    } catch (error) {
      console.error('Error loading exercise:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveExerciseResult = async (isAnswerCorrect: boolean, pointsEarned: number) => {
    if (!exercise || !student) return

    try {
      const answerText = selectedAnswer || textAnswer || JSON.stringify(matchingPairs) || JSON.stringify(orderingItems)

      const { error } = await (supabase
        .from('exercise_results') as any)
        .upsert({
          student_id: student.id,
          exercise_id: exercise.id,
          is_correct: isAnswerCorrect,
          points_earned: pointsEarned,
          answer_given: answerText
        }, {
          onConflict: 'student_id,exercise_id'
        })

      if (error) {
        console.error('Error saving exercise result:', error)
      }
    } catch (error) {
      console.error('Error saving exercise result:', error)
    }
  }

  const updateAllProgress = async () => {
    if (!contentId || !student) return

    try {
      const { count: totalCompleted, error: countError } = await supabase
        .from('exercise_results')
        .select('*', { count: 'exact', head: true })
        .eq('student_id', student.id)
        .in('exercise_id', allExercises.map(e => e.id))
        .eq('is_correct', true)

      if (countError) {
        console.error('Error counting completed exercises:', countError)
        return
      }

      const countBySkill = { listening: 0, writing: 0, reading: 0 }
      for (const skill of ['listening', 'writing', 'reading'] as const) {
        const skillExercises = allExercises.filter(e => e.skill === skill)
        if (skillExercises.length > 0) {
          const { count } = await supabase
            .from('exercise_results')
            .select('*', { count: 'exact', head: true })
            .eq('student_id', student.id)
            .in('exercise_id', skillExercises.map(e => e.id))
            .eq('is_correct', true)
          countBySkill[skill] = count || 0
        }
      }

      const totalExercises = allExercises.length
      const isContentCompleted = (totalCompleted || 0) >= totalExercises && totalExercises > 0

      const { error: upsertError } = await (supabase
        .from('student_content_progress') as any)
        .upsert({
          student_id: student.id,
          content_id: contentId,
          listening_completed: countBySkill.listening,
          writing_completed: countBySkill.writing,
          reading_completed: countBySkill.reading,
          total_exercises_completed: totalCompleted || 0,
          total_exercises: totalExercises,
          status: isContentCompleted ? 'completed' : 'in_progress',
          points_earned: score,
          started_at: new Date().toISOString(),
          completed_at: isContentCompleted ? new Date().toISOString() : null
        }, {
          onConflict: 'student_id,content_id'
        })

      if (upsertError) {
        console.error('Error updating content progress:', upsertError)
        return
      }

      // Trigger de contenido completado
      if (isContentCompleted && user) {
        triggerContentComplete(user.id, contentId)
      }

      // Always sync lesson progress to keep contents_completed accurate
      await updateLessonProgress()
    } catch (error) {
      console.error('Error updating progress:', error)
    }
  }

  const updateLessonProgress = async () => {
    if (!exercise?.content || !student) return

    try {
      const lessonId = exercise.content.lesson_id

      const { data: lessonContents } = await supabase
        .from('contents')
        .select('id')
        .eq('lesson_id', lessonId)
        .eq('is_active', true)

      const contents = lessonContents as { id: string }[] | null
      const contentIds = contents?.map(c => c.id) || []

      const { count: completedContents } = await supabase
        .from('student_content_progress')
        .select('*', { count: 'exact', head: true })
        .eq('student_id', student.id)
        .in('content_id', contentIds)
        .eq('status', 'completed')

      const totalContents = contentIds.length
      const isLessonCompleted = (completedContents || 0) >= totalContents && totalContents > 0

      // NOTE: Use update+insert instead of upsert to avoid CASE type mismatch
      // with unit_status ENUM in the after-update trigger (error 42804)
      const statusValue = isLessonCompleted ? 'completed' : 'in_progress'
      const updateData: Record<string, any> = {
        contents_completed: completedContents || 0,
        total_contents: totalContents,
        status: statusValue
      }
      if (isLessonCompleted) {
        updateData.completed_at = new Date().toISOString()
      }

      const { data: updated, error: updateError } = await (supabase
        .from('student_lesson_progress') as any)
        .update(updateData)
        .eq('student_id', student.id)
        .eq('lesson_id', lessonId)
        .select()

      if (updateError) {
        console.error('Error updating lesson progress:', updateError)
      }

      const updatedRows = updated as any[] | null
      if (!updatedRows || updatedRows.length === 0) {
        // No existing record, insert new one
        const { error: insertError } = await supabase
          .from('student_lesson_progress')
          .insert({
            student_id: student.id,
            lesson_id: lessonId,
            contents_completed: completedContents || 0,
            total_contents: totalContents,
            status: statusValue,
            started_at: new Date().toISOString(),
            ...(isLessonCompleted ? { completed_at: new Date().toISOString() } : {})
          } as any)
        if (insertError) {
          console.error('Error inserting lesson progress:', insertError)
          return
        }
      }

      if (isLessonCompleted && user) {
        triggerLessonComplete(user.id, lessonId)

        // Verificar si fue lección perfecta (100% correcto)
        const totalExInLesson = allExercises.length
        if (correctCount === totalExInLesson && totalExInLesson > 0) {
          triggerPerfectLesson(user.id, lessonId)
        }

        await updateUnitProgress(lessonId)
      }
    } catch (error) {
      console.error('Error updating lesson progress:', error)
    }
  }

  const updateUnitProgress = async (lessonId: string) => {
    if (!student) return

    try {
      const { data: lessonData } = await supabase
        .from('lessons')
        .select('unit_id')
        .eq('id', lessonId)
        .single()

      const lesson = lessonData as { unit_id: string } | null
      if (!lesson) return

      const { data: unitLessonsData } = await supabase
        .from('lessons')
        .select('id')
        .eq('unit_id', lesson.unit_id)
        .eq('is_active', true)

      const unitLessons = unitLessonsData as { id: string }[] | null
      const lessonIds = unitLessons?.map(l => l.id) || []

      const { count: completedLessons } = await supabase
        .from('student_lesson_progress')
        .select('*', { count: 'exact', head: true })
        .eq('student_id', student.id)
        .in('lesson_id', lessonIds)
        .eq('status', 'completed')

      const totalLessons = lessonIds.length
      const isUnitCompleted = (completedLessons || 0) >= totalLessons && totalLessons > 0

      // NOTE: Use update+insert instead of upsert to avoid CASE type mismatch
      // with unit_status ENUM column (error 42804)
      const unitStatus = isUnitCompleted ? 'completed' : 'in_progress'
      const unitUpdateData: Record<string, any> = {
        lessons_completed: completedLessons || 0,
        total_lessons: totalLessons,
        status: unitStatus
      }
      if (isUnitCompleted) {
        unitUpdateData.completed_at = new Date().toISOString()
      }

      const { data: unitUpdated, error: unitUpdateError } = await (supabase
        .from('student_unit_progress') as any)
        .update(unitUpdateData)
        .eq('student_id', student.id)
        .eq('unit_id', lesson.unit_id)
        .select()

      if (unitUpdateError) {
        console.error('Error updating unit progress:', unitUpdateError)
      }

      const unitUpdatedRows = unitUpdated as any[] | null
      if (!unitUpdatedRows || unitUpdatedRows.length === 0) {
        const { error: unitInsertError } = await supabase
          .from('student_unit_progress')
          .insert({
            student_id: student.id,
            unit_id: lesson.unit_id,
            lessons_completed: completedLessons || 0,
            total_lessons: totalLessons,
            status: unitStatus,
            started_at: new Date().toISOString(),
            ...(isUnitCompleted ? { completed_at: new Date().toISOString() } : {})
          } as any)
        if (unitInsertError) {
          console.error('Error inserting unit progress:', unitInsertError)
        }
      }

      // Trigger de unidad completada
      if (isUnitCompleted && user) {
        triggerUnitComplete(user.id, lesson.unit_id)
      }
    } catch (error) {
      console.error('Error updating unit progress:', error)
    }
  }

  const handleSubmit = async () => {
    if (!exercise) return

    let isAnswerCorrect = false
    const type = exercise.exercise_type

    switch (type) {
      case 'multiple_choice':
      case 'listening': {
        const answer = selectedAnswer
        const correctAnswer = exercise.correct_answer || ''
        isAnswerCorrect = answer.trim().toLowerCase() === correctAnswer.trim().toLowerCase()
        break
      }
      case 'fill_blank': {
        isAnswerCorrect = checkFillBlank(
          textAnswer,
          exercise.correct_answer || '',
          exercise.options || []
        )
        break
      }
      case 'matching': {
        const result = checkMatching(matchingPairs, exercise.options || [])
        isAnswerCorrect = result.correct === result.total
        setMatchingDetails(result.details)
        break
      }
      case 'ordering': {
        isAnswerCorrect = checkOrdering(orderingItems, exercise.options || [])
        break
      }
      case 'translation': {
        isAnswerCorrect = checkTranslation(
          textAnswer,
          exercise.correct_answer || '',
          exercise.options || []
        )
        break
      }
      default: {
        const answer = selectedAnswer || textAnswer
        isAnswerCorrect = answer.trim().toLowerCase() === (exercise.correct_answer || '').trim().toLowerCase()
      }
    }

    setIsCorrect(isAnswerCorrect)
    setIsSubmitted(true)

    const pointsEarned = isAnswerCorrect ? (exercise.points || 10) : 0

    if (isAnswerCorrect) {
      setScore(prev => prev + pointsEarned)
      setCorrectCount(prev => prev + 1)
    }

    // En modo previsualización no se guarda el resultado
    if (!isTeacherPreview) {
      await saveExerciseResult(isAnswerCorrect, pointsEarned)
    }
  }

  const handleNext = async () => {
    if (currentIndex < allExercises.length - 1) {
      const nextExercise = allExercises[currentIndex + 1]
      resetAnswerState()
      setCurrentIndex(currentIndex + 1)
      setExercise({ ...nextExercise, content: exercise?.content } as ExerciseWithContent)
      window.history.replaceState(null, '', previewPath(`/exercise/${nextExercise.id}`))
    } else {
      // Mostrar pantalla de carga mientras se calculan resultados
      setIsCalculating(true)
      try {
        // En modo previsualización no se actualiza el progreso
        if (!isTeacherPreview) {
          await updateAllProgress()
        }
      } finally {
        setIsCalculating(false)
        setShowResults(true)
      }
    }
  }

  const handleRetry = () => {
    resetAnswerState()
  }

  const resetAnswerState = () => {
    setSelectedAnswer('')
    setTextAnswer('')
    setMatchingPairs({})
    setOrderingItems([])
    setMatchingDetails({})
    setIsSubmitted(false)
    setIsCorrect(false)
  }

  const getNextContent = async () => {
    if (!exercise?.content) return null
    const currentNum = (exercise.content as any).content_number ?? (exercise.content as any).order_index ?? 0
    const { data } = await supabase
      .from('contents')
      .select('id')
      .eq('lesson_id', exercise.content.lesson_id)
      .eq('is_active', true)
      .gt('content_number', currentNum)
      .order('content_number', { ascending: true })
      .limit(1)
    const contents = data as { id: string }[] | null
    return contents?.[0]?.id || null
  }

  const getNextLesson = async () => {
    if (!exercise?.content) return null
    const { data: currentLessonData } = await supabase
      .from('lessons')
      .select('unit_id, lesson_number')
      .eq('id', exercise.content.lesson_id)
      .single()

    const currentLesson = currentLessonData as { unit_id: string; lesson_number: number } | null
    if (!currentLesson) return null

    const { data: nextLessonData } = await supabase
      .from('lessons')
      .select('id')
      .eq('unit_id', currentLesson.unit_id)
      .eq('is_active', true)
      .gt('lesson_number', currentLesson.lesson_number)
      .order('lesson_number', { ascending: true })
      .limit(1)

    const nextLesson = nextLessonData as { id: string }[] | null
    return nextLesson?.[0]?.id || null
  }

  const handleContinue = async () => {
    const nextContentId = await getNextContent()
    if (nextContentId) { navigate(previewPath(`/content/${nextContentId}`)); return }
    const nextLessonId = await getNextLesson()
    if (nextLessonId) { navigate(previewPath(`/lesson/${nextLessonId}`)); return }
    if (exercise?.content?.lesson_id) { navigate(previewPath(`/lesson/${exercise.content.lesson_id}`)) }
    else { navigate(previewPath('/units')) }
  }

  const handleRepeat = () => {
    setShowResults(false)
    setScore(0)
    setCorrectCount(0)
    setCurrentIndex(0)
    resetAnswerState()
    if (allExercises.length > 0) {
      setExercise({ ...allExercises[0], content: exercise?.content } as ExerciseWithContent)
      window.history.replaceState(null, '', previewPath(`/exercise/${allExercises[0].id}`))
    }
  }

  const getSkillIcon = (skill: string) => {
    switch (skill) {
      case 'listening': return <Headphones className="w-5 h-5" />
      case 'writing': return <PenTool className="w-5 h-5" />
      case 'reading': return <BookOpenCheck className="w-5 h-5" />
      default: return <PenTool className="w-5 h-5" />
    }
  }

  const getSkillLabel = (skill: string) => {
    switch (skill) {
      case 'listening': return 'Escucha'
      case 'writing': return 'Escritura'
      case 'reading': return 'Lectura'
      default: return skill
    }
  }

  const getSkillBgColor = (skill: string) => {
    switch (skill) {
      case 'listening': return 'bg-blue-100'
      case 'writing': return 'bg-green-100'
      case 'reading': return 'bg-orange-100'
      default: return 'bg-blue-100'
    }
  }

  const getSkillTextColor = (skill: string) => {
    switch (skill) {
      case 'listening': return 'text-blue-600'
      case 'writing': return 'text-green-600'
      case 'reading': return 'text-orange-600'
      default: return 'text-blue-600'
    }
  }

  const canSubmit = () => {
    if (!exercise) return false
    switch (exercise.exercise_type) {
      case 'multiple_choice':
      case 'listening':
        return !!selectedAnswer
      case 'fill_blank':
      case 'translation':
        return !!textAnswer.trim()
      case 'matching':
        return Object.keys(matchingPairs).length > 0
      case 'ordering':
        return orderingItems.length > 0
      default:
        return !!selectedAnswer || !!textAnswer.trim()
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (!exercise) {
    return (
      <div className="text-center py-12">
        <PenTool className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Ejercicio no encontrado</p>
        <Link to={previewPath('/units')} className="text-blue-600 hover:underline text-sm mt-2 inline-block">
          Volver a unidades
        </Link>
      </div>
    )
  }

  // Helpers para obtener números de la jerarquía
  const unitNum = exercise?.content?.lesson?.unit?.order_num ?? (exercise?.content?.lesson?.unit as any)?.unit_number
  const lessonNum = (exercise?.content?.lesson as any)?.lesson_number ?? exercise?.content?.lesson?.order_num
  const contentNum = (exercise?.content as any)?.content_number ?? (exercise?.content as any)?.order_index
  // Pantalla de cálculo de resultados
  if (isCalculating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white px-4">
        <div className="text-center max-w-sm">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-blue-100"></div>
            <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center text-3xl">📊</div>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Calculando resultados</h2>
          <p className="text-gray-500">Por favor espera un momento...</p>
          <div className="mt-4 flex justify-center gap-1">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    )
  }

  // Pantalla de resultados finales
  if (showResults) {
    const percentage = allExercises.length > 0 ? Math.round((correctCount / allExercises.length) * 100) : 0

    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
            percentage >= 70 ? 'bg-green-100' : percentage >= 50 ? 'bg-yellow-100' : 'bg-red-100'
          }`}>
            <Trophy className={`w-10 h-10 ${
              percentage >= 70 ? 'text-green-600' : percentage >= 50 ? 'text-yellow-600' : 'text-red-600'
            }`} />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {percentage >= 70 ? 'Excelente trabajo!' : percentage >= 50 ? 'Buen intento!' : 'Sigue practicando!'}
          </h1>

          {exercise?.content?.lesson && (
            <p className="text-sm text-gray-400 mb-1">
              Unidad {unitNum} &middot; Lecci&oacute;n {lessonNum} &middot; Contenido {contentNum}
            </p>
          )}
          <p className="text-gray-600 mb-6">
            Completaste los {allExercises.length} ejercicios de este contenido
          </p>

          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <div className="text-4xl font-bold text-blue-600 mb-2">{percentage}%</div>
            <p className="text-gray-500">
              {correctCount} de {allExercises.length} correctos | {score} puntos
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            {(['listening', 'writing', 'reading'] as const).map(skill => {
              const skillExercises = allExercises.filter(e => e.skill === skill)
              if (skillExercises.length === 0) return null
              return (
                <div key={skill} className={`p-3 rounded-lg ${getSkillBgColor(skill)}`}>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    {getSkillIcon(skill)}
                    <span className={`font-medium ${getSkillTextColor(skill)}`}>
                      {getSkillLabel(skill)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{skillExercises.length} ejercicios</p>
                </div>
              )
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleRepeat}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
              Repetir ejercicios
            </button>
            <button
              onClick={handleContinue}
              className="inline-flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Siguiente contenido
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  const question = exercise.question || (exercise as any).question_es || (exercise as any).question_fr || ''

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-8" translate="no" lang="fr">
      {/* Location & Progress Header */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Breadcrumb: Unidad > Lección > Contenido */}
        <div className="px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b flex items-center gap-1.5 flex-wrap">
          <Link
            to={previewPath(`/content/${exercise.content_id}`)}
            className="p-1.5 hover:bg-white/60 rounded-lg transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </Link>

          {exercise.content?.lesson?.unit && (
            <Link
              to={previewPath(`/unit/${exercise.content.lesson.unit.id}`)}
              className="flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Unidad {unitNum}</span>
              <span className="sm:hidden">U{unitNum}</span>
            </Link>
          )}
          <ChevronRight className="w-3 h-3 text-gray-400 shrink-0" />

          {exercise.content?.lesson && (
            <Link
              to={previewPath(`/lesson/${exercise.content.lesson_id}`)}
              className="text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
            >
              <span className="hidden sm:inline">Lecci&oacute;n {lessonNum}</span>
              <span className="sm:hidden">L{lessonNum}</span>
            </Link>
          )}
          <ChevronRight className="w-3 h-3 text-gray-400 shrink-0" />

          <span className="text-sm text-gray-600 font-medium">
            <span className="hidden sm:inline">Contenido {contentNum}</span>
            <span className="sm:hidden">C{contentNum}</span>
          </span>
        </div>

        {/* Exercise counter + skill + progress */}
        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold text-gray-900">
                Ejercicio {currentIndex + 1} <span className="text-gray-400 font-normal text-base">de {allExercises.length}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${getSkillBgColor(exercise.skill)} ${getSkillTextColor(exercise.skill)}`}>
                {getSkillIcon(exercise.skill)}
                <span>{getSkillLabel(exercise.skill)}</span>
              </div>
              <span className="text-sm font-semibold text-blue-600">{exercise.points || 10} pts</span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / allExercises.length) * 100}%` }}
            />
          </div>

          {/* Skill progress indicators */}
          <div className="flex gap-2 mt-3">
            {(['listening', 'writing', 'reading'] as const).map(skill => {
              const skillExercises = allExercises.filter(e => e.skill === skill)
              const isCurrentSkill = exercise.skill === skill
              const isPastSkill = allExercises.slice(0, currentIndex).some(e => e.skill === skill) && !isCurrentSkill

              if (skillExercises.length === 0) return null

              return (
                <div
                  key={skill}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                    isCurrentSkill
                      ? `${getSkillBgColor(skill)} ${getSkillTextColor(skill)}`
                      : isPastSkill
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isPastSkill && <CheckCircle className="w-3 h-3" />}
                  {getSkillLabel(skill)} ({skillExercises.length})
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Exercise Card */}
      <div className={`bg-white rounded-2xl shadow-sm overflow-hidden border-t-4 ${
        exercise.skill === 'listening' ? 'border-blue-400'
          : exercise.skill === 'writing' ? 'border-green-400'
          : exercise.skill === 'reading' ? 'border-orange-400'
          : 'border-gray-200'
      }`}>
        {/* Audio (para ejercicios con audio que NO son tipo listening - esos lo manejan internamente) */}
        {exercise.audio_url && exercise.exercise_type !== 'listening' && (
          <div className="p-4 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center gap-3 mb-2">
              <Volume2 className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Escucha el audio</span>
            </div>
            <AudioPlayer src={exercise.audio_url} className="w-full" />
          </div>
        )}

        {/* Imagen */}
        {exercise.image_url && (
          <div className="p-4 bg-gray-50 border-b">
            <img src={exercise.image_url} alt="Ejercicio" className="max-h-48 mx-auto rounded-lg" />
          </div>
        )}

        <div className="p-6">
          {/* Pregunta (solo para tipos que no la manejan internamente) */}
          {exercise.exercise_type !== 'fill_blank' &&
           exercise.exercise_type !== 'matching' &&
           exercise.exercise_type !== 'ordering' &&
           exercise.exercise_type !== 'translation' &&
           exercise.exercise_type !== 'listening' && (
            <EditableText
              as="h2"
              table="exercises"
              id={exercise.id}
              field="question"
              value={question}
              placeholder="(sin pregunta)"
              multiline
              className="text-xl font-semibold text-gray-900 mb-6 block"
              onSaved={(v) => setExercise({ ...exercise, question: v } as ExerciseWithContent)}
            />
          )}

          {/* Renderizado dinamico por tipo */}
          {exercise.exercise_type === 'multiple_choice' && (
            <MultipleChoiceExercise
              exercise={exercise}
              selectedAnswer={selectedAnswer}
              onSelectAnswer={setSelectedAnswer}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onExerciseUpdated={(u) => setExercise(u as ExerciseWithContent)}
            />
          )}

          {exercise.exercise_type === 'fill_blank' && (
            <FillBlankExercise
              exercise={exercise}
              textAnswer={textAnswer}
              onChangeAnswer={setTextAnswer}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onExerciseUpdated={(u) => setExercise(u as ExerciseWithContent)}
            />
          )}

          {exercise.exercise_type === 'matching' && (
            <MatchingExercise
              exercise={exercise}
              onSetPairs={setMatchingPairs}
              isSubmitted={isSubmitted}
              correctDetails={matchingDetails}
              skill={exercise.skill}
              onExerciseUpdated={(u) => setExercise(u as ExerciseWithContent)}
            />
          )}

          {exercise.exercise_type === 'ordering' && (
            <OrderingExercise
              exercise={exercise}
              onSetOrder={setOrderingItems}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onExerciseUpdated={(u) => setExercise(u as ExerciseWithContent)}
            />
          )}

          {exercise.exercise_type === 'translation' && (
            <TranslationExercise
              exercise={exercise}
              textAnswer={textAnswer}
              onChangeAnswer={setTextAnswer}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onExerciseUpdated={(u) => setExercise(u as ExerciseWithContent)}
            />
          )}

          {exercise.exercise_type === 'listening' && (
            <ListeningExercise
              exercise={exercise}
              selectedAnswer={selectedAnswer}
              onSelectAnswer={setSelectedAnswer}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onExerciseUpdated={(u) => setExercise(u as ExerciseWithContent)}
            />
          )}

          {/* Explicacion */}
          {isSubmitted && exercise.explanation && (
            <div className={`mt-4 p-4 rounded-xl ${isCorrect ? 'bg-green-50' : 'bg-yellow-50'}`}>
              <p className="text-sm font-medium text-gray-700 mb-1">Explicación:</p>
              <EditableText
                as="p"
                table="exercises"
                id={exercise.id}
                field="explanation"
                value={exercise.explanation}
                placeholder="(sin explicación)"
                multiline
                className="text-gray-600 block"
                onSaved={(v) => setExercise({ ...exercise, explanation: v } as ExerciseWithContent)}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 bg-gray-50 border-t">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit()}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Comprobar respuesta
            </button>
          ) : (
            <div className="flex gap-3">
              {!isCorrect && (
                <button
                  onClick={handleRetry}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors"
                >
                  Reintentar
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={isCalculating}
                className={`flex-1 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
                  isCalculating
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isCalculating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Calculando...
                  </>
                ) : (
                  <>
                    {currentIndex < allExercises.length - 1 ? 'Siguiente' : 'Ver resultados'}
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  )
}
